import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ShareLinkService } from '../share-link-service';
import {
    CreatePublicShareLinkApi,
} from '@telecom/shared/infrastructure/api-functions/create-public-share-link-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { isInIframe } from '@telecom/shared/utils/in-iframe';

/**
 * Implementation of ShareLinkService
 */
@injectable()
export class ShareLinkServiceImpl implements ShareLinkService {
    constructor(
        @inject(CreatePublicShareLinkApi)
        private _createPublicShareLinkApi: CreatePublicShareLinkApi,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
    ) {}

    private get _taskId() {
        return this._currentTaskService.taskId;
    }

    /**
     * Get the appropriate window object (handles iframe scenario)
     */
    private _getTargetWindow(): Window {
        return isInIframe() ? window.parent : window;
    }

    /**
     * Build share URL with hash-based routing
     *
     * @param baseUrl - Base URL without hash
     * @param hash - Hash part of the URL
     * @param shareId - Share ID to include in the URL
     */
    private _buildHashBasedUrl(baseUrl: string, hash: string, shareId: string): string {
        const hashParts = hash.split('?');
        const hashPath = hashParts[0]; // e.g., '#/dashboard'
        const existingParams = hashParts[1]; // e.g., 'tab=overview&filter=active'

        const taskIdParam = `sh_${shareId}`;

        if (existingParams) {
            // Replace or add taskId parameter, remove old task_id parameter
            const params = new URLSearchParams(existingParams);
            params.delete('task_id'); // Remove old task_id parameter
            params.set('taskId', taskIdParam);
            return `${baseUrl}${hashPath}?${params.toString()}`;
        }

        // No existing parameters, add taskId directly
        return `${baseUrl}${hashPath}?taskId=${taskIdParam}`;
    }

    /**
     * Build share URL with path-based routing
     *
     * @param location - Window location object
     * @param shareId - Share ID to replace the task ID in the path
     */
    private _buildPathBasedUrl(location: Location, shareId: string): string {
        const pathSegments = location.pathname.split('/');
        // Replace the last path segment (task ID) with share ID
        pathSegments[pathSegments.length - 1] = `sh_${shareId}`;
        const newPathname = pathSegments.join('/');
        return `${location.origin}${newPathname}${location.search}`;
    }

    /**
     * Create a share link for a given task
     *
     * @param password - Optional password for protected sharing
     * @returns Promise resolving to the complete share URL
     * @throws Error if the share link creation fails
     */
    async createShareLink(password?: string): Promise<string> {
        try {
            const response = await this._createPublicShareLinkApi.createPublicShareLink({
                body: {
                    task_id: this._taskId,
                    password: password,
                },
            });

            const shareId = response.share_id;
            const $window = this._getTargetWindow();
            const { origin, pathname, search, hash } = $window.location;

            if (hash) {
                const baseUrl = origin + pathname + search;
                return this._buildHashBasedUrl(baseUrl, hash, shareId);
            }

            return this._buildPathBasedUrl($window.location, shareId);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new Error(`Failed to create share link for task ${this._taskId}: ${errorMessage}`);
        }
    }
}
